import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Tab,
  TabPanel,
  Tabs,
  TabsBody,
  TabsHeader,
  Typography,
} from "@material-tailwind/react";
import Label from "components/Label/Label";
import React, { useState } from "react";
import ButtonSecondary from "shared/Button/ButtonSecondary";

interface ReclamationReservationDetailsProps {
  open: boolean;
  handleClose?: () => void;
  data: {
    id?: number;
    reference?: string;
    sujet?: string;
    description?: string;
    clientId?: number;
    ownerId?: number;
    status?: "pending" | "released";
    enable?: boolean;
    createdAt?: string;
    updatedAt?: string;
    images?: string;
  } | null;
}

const ReclamationReservationDetails: React.FC<ReclamationReservationDetailsProps> = ({
  open,
  handleClose,
  data,
}) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false); // State for image preview dialog

  const formatDescription = (description?: string) => {
    if (!description) return "N/A";
    return description.replace(/\r\n/g, " ").replace(/\s+/g, " ").trim();
  };

  const fieldIcons = {
    reference: "las la-tag",
    sujet: "las la-heading",
    description: "las la-comment",
    clientId: "las la-user",
    ownerId: "las la-user-tie",
    status: "las la-info-circle",
    enable: "las la-toggle-on",
    createdAt: "las la-calendar-plus",
    updatedAt: "las la-calendar-check",
    image: "las la-image",
  };

  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Détails", value: "details" },
    { label: "Image", value: "image" },
  ];

  const handleImageClick = () => {
    if (data?.images) {
      setIsImagePreviewOpen(true); // Open image preview dialog
    }
  };

  const handleCloseImagePreview = () => {
    setIsImagePreviewOpen(false); // Close image preview dialog
  };

  return (
    <>
      <Dialog
        open={open}
        handler={() => {}}
        className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
      >
        <DialogHeader className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
          <Typography variant="h5" className="font-bold tracking-tight text-center text-white animate-fade-in">
            Détails de la Réclamation
          </Typography>
        </DialogHeader>
        <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
          <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
            <TabsHeader className="p-1 bg-gray-100 rounded-lg dark:bg-gray-800">
              {tabs.map(({ label, value }) => (
                <Tab key={value} value={value} className="p-2 text-sm font-semibold">
                  {label}
                </Tab>
              ))}
            </TabsHeader>
            <TabsBody className="mt-4">
              {/* Basic Information Tab */}
              <TabPanel value="basic" className="z-10 space-y- elative">
                {[
                  { label: "Référence", value: data?.reference, icon: fieldIcons.reference },
                  { label: "Sujet", value: data?.sujet, icon: fieldIcons.sujet },
                  {
                    label: "Status",
                    value: data?.status === "pending" ? "En cours" : "Libéré",
                    icon: fieldIcons.status,
                  },
                  {
                    label: "Créé le",
                    value: data?.createdAt ? new Date(data.createdAt).toLocaleDateString("fr-FR") : "N/A",
                    icon: fieldIcons.createdAt,
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                  >
                    <div className="flex items-center space-x-2">
                      <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                      <Label className="z-10 font-semibold text-gray-700 dark:text-gray-let relative200">
                        {item.label} :
                      </Label>
                    </div>
                    <Typography className="font-medium text-gray-900 dark:text-white">
                      {item.value || "N/A"}
                    </Typography>
                  </div>
                ))}
              </TabPanel>

              {/* Details Tab */}
              <TabPanel value="details" className="space-y-4">
                {[
                  {
                    label: "Description",
                    value: formatDescription(data?.description),
                    icon: fieldIcons.description,
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                      </div>
                      <Typography className="font-medium text-gray-900 dark:text-white">
                        {item.value || "N/A"}
                      </Typography>
                    </div>
                  </div>
                ))}
              </TabPanel>

              {/* Image Tab */}
              <TabPanel value="image" className="space-y-4">
                <div className="p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md">
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                      <i className={`${fieldIcons.image} text-xl text-blue-500 dark:text-blue-400`}></i>
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Image :</Label>
                    </div>
                    {data?.images ? (
                      <img
                        src={process.env.REACT_APP_CLOUD_FRONT + data.images}
                        alt="Reclamation"
                        className="w-full max-w-md mx-auto rounded-lg shadow-md cursor-pointer"
                        onError={(e) => {
                          e.currentTarget.src = "https://via.placeholder.com/400x300?text=Image+non+disponible";
                        }}
                        onClick={handleImageClick} // Trigger image preview on click
                      />
                    ) : (
                      <Typography className="font-medium text-gray-900 dark:text-white">
                        Aucune image disponible
                      </Typography>
                    )}
                  </div>
                </div>
              </TabPanel>
            </TabsBody>
          </Tabs>
        </DialogBody>
        <DialogFooter className="flex justify-center p-4 space-x-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
          <ButtonSecondary
            onClick={handleClose}
            className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Quitter
          </ButtonSecondary>
        </DialogFooter>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={isImagePreviewOpen}
        handler={handleCloseImagePreview}
        className="w-full max-w-4xl mx-auto bg-transparent"
      >
        <DialogBody className="p-0">
          <img
            src={
              data?.images
                ? process.env.REACT_APP_CLOUD_FRONT + data.images
                : "https://via.placeholder.com/400x300?text=Image+non+disponible"
            }
            alt="Reclamation Preview"
            className="w-full max-h-[80vh] object-contain rounded-lg"
            onError={(e) => {
              e.currentTarget.src = "https://via.placeholder.com/400x300?text=Image+non+disponible";
            }}
          />
        </DialogBody>
      </Dialog>
    </>
  );
};

export default ReclamationReservationDetails;
